import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardCheck,
  MousePointerClick,
  MonitorSmartphone,
  ShoppingBag,
  TrendingUp,
  Bot,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

const steps = [
  {
    icon: ClipboardCheck,
    title: "Register Business",
    body: "Tell us your business name, category and contact details. Our team verifies you on WhatsApp in minutes.",
    tone: "from-amber-500/25 to-amber-500/0",
    iconColor: "text-amber-300",
  },
  {
    icon: MousePointerClick,
    title: "Go Online — One Click",
    body: "Pick a plan. Pay securely via UPI. That's it — your digital launch is scheduled.",
    tone: "from-blue-500/25 to-blue-500/0",
    iconColor: "text-blue-300",
  },
  {
    icon: MonitorSmartphone,
    title: "Website & App Created",
    body: "Your branded site, mobile app, payment gateway and AI assistant go live in just 2 days.",
    tone: "from-violet-500/25 to-violet-500/0",
    iconColor: "text-violet-300",
  },
  {
    icon: ShoppingBag,
    title: "Orders Start Flowing",
    body: "Customers discover, order and pay — on UPI, cards or wallets. You focus on fulfilling, not configuring.",
    tone: "from-pink-500/25 to-pink-500/0",
    iconColor: "text-pink-300",
  },
  {
    icon: TrendingUp,
    title: "Business Growth",
    body: "Live dashboard, analytics, offers and retargeting. 3× revenue lift. 10× audience. Pure compounding.",
    tone: "from-emerald-500/25 to-emerald-500/0",
    iconColor: "text-emerald-300",
  },
];

const AUTO_MS = 4500;

export default function AIRobotGuide({ onCta }) {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const trackRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) return;
    timer.current = setInterval(() => {
      setActive((a) => (a + 1) % steps.length);
    }, AUTO_MS);
    return () => clearInterval(timer.current);
  }, [playing]);

  const go = (i) => {
    setActive(((i % steps.length) + steps.length) % steps.length);
    setPlaying(false);
  };

  const robotLeftPct = (active / (steps.length - 1)) * 100;

  return (
    <section
      id="ai-guide"
      data-testid="ai-guide-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] h-[75%] radial-glow-violet blur-3xl opacity-50" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="uppercase text-xs tracking-[0.24em] text-violet-300 font-semibold">
            AI Robot Guide
          </p>
          <h2
            data-testid="ai-guide-heading"
            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
          >
            Your <span className="text-gradient-neon">Digital Assistant</span> Guides You.
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Five simple steps, zero confusion. Aria walks you through the entire
            journey — from registration to revenue.
          </p>
        </div>

        {/* Robot + progress */}
        <div className="relative mt-16 max-w-5xl mx-auto">
          <div className="relative h-16">
            {/* Progress line */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[3px] rounded-full bg-white/5 overflow-hidden">
              <motion.div
                className="h-full btn-primary-grad"
                initial={false}
                animate={{ width: `${(active / (steps.length - 1)) * 100}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            {/* Step dots */}
            <div className="relative h-full flex items-center justify-between">
              {steps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  data-testid={`ai-guide-dot-${i}`}
                  aria-label={`Go to step ${i + 1}`}
                  className={`relative w-5 h-5 rounded-full border transition-all ${
                    i <= active
                      ? "bg-white border-white shadow-[0_0_14px_rgba(167,139,250,0.65)]"
                      : "bg-[#0b0c1d] border-white/20"
                  }`}
                >
                  {i === active && (
                    <span className="absolute inset-0 rounded-full bg-violet-400/50 animate-soft-ping" />
                  )}
                </button>
              ))}
            </div>
            {/* Robot */}
            <motion.div
              initial={false}
              animate={{ left: `${robotLeftPct}%` }}
              transition={{ type: "spring", stiffness: 80, damping: 14 }}
              className="absolute -top-14 -translate-x-1/2"
              data-testid="ai-guide-robot"
            >
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl btn-primary-grad flex items-center justify-center shadow-xl shadow-violet-500/50 animate-floaty">
                  <Bot className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-2 h-2 rotate-45 btn-primary-grad" />
              </div>
            </motion.div>
          </div>

          {/* Step labels */}
          <div className="mt-6 hidden md:grid grid-cols-5 gap-2 text-center">
            {steps.map((s, i) => (
              <button
                key={s.title}
                onClick={() => go(i)}
                className={`text-[11px] uppercase tracking-widest transition-colors ${
                  i === active ? "text-white font-semibold" : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Step {i + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Active step card */}
        <div className="mt-10 relative max-w-3xl mx-auto min-h-[280px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              ref={trackRef}
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -24, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`ai-guide-step-${active}`}
              className="relative rounded-3xl glass-strong border border-white/10 p-8 sm:p-10 overflow-hidden"
            >
              <div className={`absolute -top-24 -right-20 w-72 h-72 bg-gradient-to-br ${steps[active].tone} blur-3xl rounded-full opacity-80`} />
              <div className="relative flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                <div className="w-16 h-16 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center animate-floaty shrink-0">
                  {React.createElement(steps[active].icon, {
                    className: `w-8 h-8 ${steps[active].iconColor}`,
                    strokeWidth: 1.6,
                  })}
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.24em] text-violet-300 font-semibold">
                    Step {active + 1} of {steps.length}
                  </p>
                  <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold">
                    {steps[active].title}
                  </h3>
                  <p className="mt-3 text-slate-400 leading-relaxed">
                    {steps[active].body}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            onClick={() => go(active - 1)}
            data-testid="ai-guide-prev"
            className="w-10 h-10 rounded-full glass border border-white/10 flex items-center justify-center hover:border-violet-500/50 transition-colors"
            aria-label="Previous step"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPlaying((p) => !p)}
            data-testid="ai-guide-autoplay"
            className="px-4 py-2 rounded-full glass border border-white/10 text-xs uppercase tracking-widest hover:border-violet-500/50 transition-colors"
          >
            {playing ? "Pause autoplay" : "Play autoplay"}
          </button>
          <button
            onClick={() => go(active + 1)}
            data-testid="ai-guide-next"
            className="w-10 h-10 rounded-full glass border border-white/10 flex items-center justify-center hover:border-violet-500/50 transition-colors"
            aria-label="Next step"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-12 flex justify-center">
          <button
            onClick={onCta}
            data-testid="ai-guide-cta"
            className="group px-7 py-3.5 rounded-full btn-primary-grad text-white font-semibold text-base transition-all hover:-translate-y-0.5 shadow-2xl shadow-violet-500/40 flex items-center gap-2"
          >
            Start Your Online Journey
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}
